/**
 * Configuración de múltiples PACS (Picture Archiving and Communication System)
 *
 * Cada PACS puede incluir:
 * - IP, PUERTO, AETITLE, USUARIO, PASS, PROTOCOLO, BASE_PATH
 * - TOOLBAR_LAYOUT — opciones:
 *     'toolbarClassic' | TOOLBAR_LAYOUTS.CLASSIC  → barra superior clásica
 *     'toolbarEdge'    | TOOLBAR_LAYOUTS.EDGE     → panel edge (móvil compacto)
 *     'toolbarStudio'  | TOOLBAR_LAYOUTS.STUDIO   → Studio Rail (desktop)
 *     'toolbarOhif'    | TOOLBAR_LAYOUTS.OHIF     → OHIF canvas-first (desktop)
 *     'toolbarOviyamNg'| TOOLBAR_LAYOUTS.OVIYAM_NG → OviyamNG (Mayam + series + tools)
 *     'toolbarOviyam'  | TOOLBAR_LAYOUTS.OVIYAM    → alias legacy → OVIYAM_NG

 * - DEFAULT_RENDER_MODE — modo inicial si el manifest trae dcm + preview:
 *     'PREVIEW' | 'P' | 'RAPIDO' | 'FAST'           → previews (webp/jpg)
 *     'DIAGNOSTIC' | 'DIAG' | 'D' | 'DICOM' | 'DCM' → DICOM real
 *   (si se omite o valor inválido → heurística device/red)
 *   Prioridad al cargar: ?renderMode= → userSet → DEFAULT_RENDER_MODE → device
 *   (si solo hay dcm o solo preview, caps fuerza el modo viable)
 * - R2_DIRECT / R2_MANIFEST_PATH: hybrid Orthanc + presigner
 * - R2_PATH_PREFIXES: prefijos de path en CDN R2 asociados a este PACS
 *   (p. ej. ['tjsiddse'] → estudios en …/tjsiddse/.../manifest.json usan este
 *   PROFILE para toolbar/auth aunque la URL del visor no lleve ?pacs=)
 *
 * Query del visor (overrides):
 *   ?pacs=DEMOPORTAL&studyId=...
 *   ?manifestUrl=...                         (pacs se deduce por R2_PATH_PREFIXES)
 *   ?manifestUrl=...&pacs=DEMOPORTAL         (pacs explícito; toolbar/auth de ese PACS)
 *   ?toolbar=edge|classic|studio|ohif|oviyamng|oviyam|toolbarEdge|…
 *   ?shell=classic|edge|studio|ohif|oviyamng|oviyam   (alias preferido de toolbar)
 *   ?theme=dark|brand|light                  (paleta chrome OHIF/Oviyam)
 *   ?udvToken=…&plan=lite|pro|premium  (acceso + plan; ver docs/MANUAL-PLANES-UDV.md)
 *   ?renderMode=PREVIEW|DIAGNOSTIC|DIAG|DICOM|P|D
 */

/** Catálogo de layouts de barra (extensible). */
export const TOOLBAR_LAYOUTS = {
  /** toolbarClassic — AppBar + tira de tools */
  CLASSIC: 'toolbarClassic',
  /** toolbarEdge — drawer/ghost rail (shell edge en viewport < 1024) */
  EDGE: 'toolbarEdge',
  /** toolbarStudio — Studio Rail: series izq. + viewport + tools der. (desktop) */
  STUDIO: 'toolbarStudio',
  /** toolbarOhif — canvas-first: topbar mínima + drawer series + floating tools */
  OHIF: 'toolbarOhif',
  /** toolbarOviyamNg — Oviyam Next Gen (Mayam chrome + series + tools) */
  OVIYAM_NG: 'toolbarOviyamNg',
  /** @deprecated alias → OVIYAM_NG */
  OVIYAM: 'toolbarOviyamNg',
}

/** Path por defecto del presigner (nginx → microservicio Node). */
export const DEFAULT_R2_MANIFEST_PATH = '/studies/{id}/r2-manifest'

export const PACS_CONFIG = {
  LOSALISOS: {
    IP: 'losalisos.tanjousoft.com.ar',
    PUERTO: '443',
    AETITLE: 'TJSLOSALISOS',
    USUARIO: 'orthanc',
    PASS: 'orthanc',
    PROTOCOLO: 'https',
    BASE_PATH: '/visorweb',
    // TOOLBAR: toolbarClassic | toolbarEdge
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.CLASSIC,
    // RENDER: PREVIEW | DIAGNOSTIC | DICOM (aliases: P, DIAG, D, DCM, RAPIDO, FAST)
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_PATH_PREFIXES: [],
  },

  IDIMAGENES: {
    IP: 'idimagenes.tanjousoft.com.ar',
    PUERTO: '443',
    AETITLE: 'TJSIDIMAGENES',
    USUARIO: 'orthanc',
    PASS: 'orthanc',
    PROTOCOLO: 'https',
    BASE_PATH: '/visorweb',
    // TOOLBAR: toolbarClassic | toolbarEdge
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.CLASSIC,
    // RENDER: PREVIEW | DIAGNOSTIC | DICOM (aliases: P, DIAG, D, DCM, RAPIDO, FAST)
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_PATH_PREFIXES: [],
  },

  TACSE: {
    IP: 'plataforma.tacse.com.ar',
    PUERTO: '443',
    AETITLE: 'TJSTACSE',
    USUARIO: 'orthanc',
    PASS: 'orthanc',
    PROTOCOLO: 'https',
    BASE_PATH: '/visorweb',
    // TOOLBAR: toolbarClassic | toolbarEdge
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.CLASSIC,
    // RENDER: PREVIEW | DIAGNOSTIC | DICOM (aliases: P, DIAG, D, DCM, RAPIDO, FAST)
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_PATH_PREFIXES: [],
  },

  // Demo: Studio en desktop; Edge en móvil (<1024)
  DEMOPORTAL: {
    IP: 'demoportal.tanjousoft.com.ar',
    PUERTO: '443',
    AETITLE: 'ORTHANC',
    USUARIO: 'orthanc',
    PASS: 'orthanc',
    PROTOCOLO: 'https',
    BASE_PATH: '/visorweb',
    // TOOLBAR: toolbarClassic | toolbarEdge | toolbarStudio
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.STUDIO,
    // RENDER: PREVIEW | DIAGNOSTIC | DICOM (aliases: P, DIAG, D, DCM, RAPIDO, FAST)
    DEFAULT_RENDER_MODE: 'PREVIEW',
    R2_DIRECT: false,
    R2_MANIFEST_PATH: DEFAULT_R2_MANIFEST_PATH,
    // Prefijo en CDN público: …/tjsiddse/{studyUID}/manifest.json
    R2_PATH_PREFIXES: ['tjsiddse'],
  },

  // IDDSE (plataforma central) — Orthanc vía webportal
  IDDSE: {
    IP: 'webportal.iddse.com.ar',
    PUERTO: '443',
    AETITLE: 'IDDSE',
    USUARIO: 'iddse',
    PASS: 'iddse263',
    PROTOCOLO: 'https',
    BASE_PATH: '/visorweb',
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.STUDIO,
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_MANIFEST_PATH: DEFAULT_R2_MANIFEST_PATH,
    R2_PATH_PREFIXES: ['tjsiddse'],
  },

  // Sanatorio Marcos Paz — Orthanc VPS ZipRangeVFS (hub)
  MARCOSPAZ: {
    IP: 'pacssanatoriomarcospaz.portaldiagnostico.com.ar',
    PUERTO: '443',
    AETITLE: 'TJSMARCOSPAZ',
    USUARIO: 'admin',
    PASS: 'tjs2026',
    PROTOCOLO: 'https',
    BASE_PATH: '',
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.STUDIO,
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_MANIFEST_PATH: DEFAULT_R2_MANIFEST_PATH,
    // Prefijo R2 ZipRangeVFS
    R2_PATH_PREFIXES: ['sanatoriomarcospazTUC'],
  },

  // IC Imágenes — Orthanc VPS ZipRangeVFS (hub Path M)
  // PACS público: configurar NPM → tjsicimagenes-cors:80 cuando exista el dominio.
  ICIMAGENES: {
    IP: 'pacsicimagenes.portaldiagnostico.com.ar',
    PUERTO: '443',
    AETITLE: 'TJSICIMAGENES',
    USUARIO: 'admin',
    PASS: 'tjs2026',
    PROTOCOLO: 'https',
    BASE_PATH: '',
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.STUDIO,
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_MANIFEST_PATH: DEFAULT_R2_MANIFEST_PATH,
    R2_PATH_PREFIXES: ['icimagenesVFS'],
  },

  DEFAULT: {
    IP: 'demoportal.tanjousoft.com.ar',
    PUERTO: '443',
    AETITLE: 'ORTHANC',
    USUARIO: 'orthanc',
    PASS: 'orthanc',
    PROTOCOLO: 'https',
    BASE_PATH: '/visorweb',
    // TOOLBAR: toolbarClassic | toolbarEdge | toolbarStudio
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.STUDIO,
    // RENDER: PREVIEW | DIAGNOSTIC | DICOM (aliases: P, DIAG, D, DCM, RAPIDO, FAST)
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_PATH_PREFIXES: [],
  },

  // Clave canónica del perfil (usar en links: ?pacs=ZIPRANGEVFS)
  ZIPRANGEVFS: {
    IP: 'ziprangevfspacs.tanjousoft.com.ar',
    PUERTO: '443',
    AETITLE: 'TJSPLATAFORMAVFS',
    USUARIO: 'admin',
    PASS: 'tjs2026',
    PROTOCOLO: 'https',
    BASE_PATH: '',
    // TOOLBAR: toolbarClassic | toolbarEdge
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.EDGE,
    // RENDER: PREVIEW | DIAGNOSTIC | DICOM (aliases: P, DIAG, D, DCM, RAPIDO, FAST)
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_PATH_PREFIXES: [],
  },

  /** dcm4chee 2.18.x (O-NG): QIDO-RS + WADO-URI clásico, sin Orthanc REST. */
  DCM4CHEE: {
    IP: '192.168.1.100',
    PUERTO: '8080',
    AETITLE: 'DCM4CHEE',
    USUARIO: 'admin',
    PASS: 'admin',
    PROTOCOLO: 'http',
    BASE_PATH: '',
    WADO_URI_PATH: '/wado',
    DICOMWEB_BASE: '/dicom-web',
    TOOLBAR_LAYOUT: TOOLBAR_LAYOUTS.CLASSIC,
    DEFAULT_RENDER_MODE: 'DIAGNOSTIC',
    R2_DIRECT: false,
    R2_PATH_PREFIXES: [],
  },
}

// Alias: links legacy usan el AET (?pacs=TJSPLATAFORMAVFS) en vez de ZIPRANGEVFS.
// Sin esto getPacsConfig cae a DEFAULT → demoportal y 404.
PACS_CONFIG.TJSPLATAFORMAVFS = PACS_CONFIG.ZIPRANGEVFS

/**
 * Índice prefijo R2 (minúsculas) → pacsId.
 * Se arma desde R2_PATH_PREFIXES de cada entrada de PACS_CONFIG.
 */
export function buildR2PathPrefixIndex() {
  const map = Object.create(null)
  Object.keys(PACS_CONFIG).forEach((pacsId) => {
    if (pacsId === 'DEFAULT') return
    const prefixes = PACS_CONFIG[pacsId].R2_PATH_PREFIXES || []
    prefixes.forEach((p) => {
      const key = String(p || '')
        .trim()
        .toLowerCase()
      if (key) map[key] = pacsId
    })
  })
  return map
}

/**
 * Primer segmento útil del path de un manifest CDN
 * (ignora 'manifest.json' y vacíos).
 * @param {string} manifestUrl
 * @returns {string|null} p.ej. 'tjsiddse'
 */
export function extractR2PathPrefixFromManifestUrl(manifestUrl) {
  if (!manifestUrl) return null
  try {
    const u = new URL(manifestUrl)
    const segs = u.pathname.split('/').filter(Boolean)
    if (segs.length === 0) return null
    // path típico: /tjsiddse/{studyUID}/manifest.json
    const first = segs[0]
    if (!first || /\.json$/i.test(first)) return null
    return first.toLowerCase()
  } catch (e) {
    return null
  }
}

/**
 * ¿Valor de query aún no sustituido por la plataforma?
 * p. ej. {pacs}, {studyId}, NONE, vacío.
 * @param {string|null|undefined} raw
 * @returns {boolean}
 */
export function isUrlParamPlaceholder(raw) {
  if (raw == null) return true
  const s = String(raw).trim()
  if (!s) return true
  if (s.charAt(0) === '{' && s.charAt(s.length - 1) === '}') return true
  const lower = s.toLowerCase()
  return (
    lower === 'none' ||
    lower === 'sinpacs' ||
    lower === 'placeholder' ||
    lower === '_'
  )
}

/**
 * Resuelve pacsId efectivo:
 * 1) ?pacs= explícito (no placeholder)
 * 2) prefijo path del manifestUrl vía R2_PATH_PREFIXES
 * 3) null (sin PACS — no fuerza DEFAULT)
 *
 * @param {{ pacsId?: string|null, manifestUrl?: string|null }} ctx
 * @returns {string|null}
 */
export function resolveEffectivePacsId(ctx = {}) {
  const explicitRaw = ctx.pacsId ? String(ctx.pacsId).trim() : ''
  const explicit = isUrlParamPlaceholder(explicitRaw) ? '' : explicitRaw
  if (explicit) {
    const upper = explicit.toUpperCase()
    if (PACS_CONFIG[upper]) return upper
    console.warn(`PACS '${explicit}' no encontrado. Se intentará inferencia R2 / DEFAULT.`)
  }

  const prefix = extractR2PathPrefixFromManifestUrl(ctx.manifestUrl)
  if (prefix) {
    const index = buildR2PathPrefixIndex()
    if (index[prefix]) {
      return index[prefix]
    }
  }

  return explicit ? explicit.toUpperCase() : null
}

/**
 * Parse ?toolbar=edge|classic|studio|ohif|oviyam|toolbarEdge|…
 * @param {string|null} raw
 * @returns {string|null} valor TOOLBAR_LAYOUTS o null
 */
export function parseToolbarQueryParam(raw) {
  if (raw == null || raw === '') return null
  const v = String(raw).trim().toLowerCase()
  if (v === 'edge' || v === 'toolbaredge' || v === TOOLBAR_LAYOUTS.EDGE.toLowerCase()) {
    return TOOLBAR_LAYOUTS.EDGE
  }
  if (
    v === 'classic' ||
    v === 'toolbarclassic' ||
    v === TOOLBAR_LAYOUTS.CLASSIC.toLowerCase()
  ) {
    return TOOLBAR_LAYOUTS.CLASSIC
  }
  if (
    v === 'studio' ||
    v === 'toolbarstudio' ||
    v === TOOLBAR_LAYOUTS.STUDIO.toLowerCase()
  ) {
    return TOOLBAR_LAYOUTS.STUDIO
  }
  if (
    v === 'ohif' ||
    v === 'toolbarohif' ||
    v === TOOLBAR_LAYOUTS.OHIF.toLowerCase()
  ) {
    return TOOLBAR_LAYOUTS.OHIF
  }
  if (
    v === 'oviyamng' ||
    v === 'oviyam-ng' ||
    v === 'toolbaroviyamng' ||
    v === 'oviyam' ||
    v === 'toolbaroviyam' ||
    v === TOOLBAR_LAYOUTS.OVIYAM_NG.toLowerCase()
  ) {
    return TOOLBAR_LAYOUTS.OVIYAM_NG
  }
  console.warn(`toolbar param desconocido: '${raw}'. Usando layout del PACS.`)
  return null
}

/**
 * Obtiene la configuración de un PACS específico
 * @param {string} pacsId - ID del PACS (ej: 'DEMOPORTAL', 'DEFAULT')
 * @returns {Object} Configuración del PACS o DEFAULT si no existe
 */
export const getPacsConfig = (pacsId) => {
  if (!pacsId) {
    return PACS_CONFIG.DEFAULT
  }

  const pacsIdUpper = pacsId.toUpperCase()

  if (PACS_CONFIG[pacsIdUpper]) {
    return PACS_CONFIG[pacsIdUpper]
  }

  console.warn(`PACS '${pacsId}' no encontrado en configuración. Usando DEFAULT.`)
  return PACS_CONFIG.DEFAULT
}

/**
 * Normaliza DEFAULT_RENDER_MODE del PACS.
 * @param {string|null|undefined} raw
 * @returns {'PREVIEW'|'DIAGNOSTIC'|null} null = sin override (device)
 */
export function parsePacsDefaultRenderMode(raw) {
  if (raw == null || raw === '') return null
  const s = String(raw).trim().toUpperCase()
  if (s === 'PREVIEW' || s === 'P' || s === 'RAPIDO' || s === 'FAST') return 'PREVIEW'
  if (
    s === 'DIAGNOSTIC' ||
    s === 'DIAG' ||
    s === 'D' ||
    s === 'DICOM' ||
    s === 'DCM'
  ) {
    return 'DIAGNOSTIC'
  }
  console.warn(
    `DEFAULT_RENDER_MODE desconocido: '${raw}'. Usando heurística de device.`
  )
  return null
}

/**
 * DEFAULT_RENDER_MODE del PACS efectivo.
 * @param {string|null} pacsId
 * @returns {'PREVIEW'|'DIAGNOSTIC'|null}
 */
export function resolvePacsDefaultRenderMode(pacsId) {
  const cfg = getPacsConfig(pacsId)
  return parsePacsDefaultRenderMode(cfg && cfg.DEFAULT_RENDER_MODE)
}

/**
 * Resuelve layout de barra.
 * Prioridad: ?toolbar= override → TOOLBAR_LAYOUT del PACS → classic.
 *
 * @param {string|null} pacsId
 * @param {string|null} [toolbarOverride] valor de TOOLBAR_LAYOUTS (parseToolbarQueryParam)
 * @returns {string}
 */
export const resolveToolbarLayout = (pacsId, toolbarOverride = null) => {
  const allowed = Object.values(TOOLBAR_LAYOUTS)
  if (toolbarOverride && allowed.includes(toolbarOverride)) {
    return toolbarOverride
  }
  const cfg = getPacsConfig(pacsId)
  const layout = cfg.TOOLBAR_LAYOUT || TOOLBAR_LAYOUTS.CLASSIC
  return allowed.includes(layout) ? layout : TOOLBAR_LAYOUTS.CLASSIC
}

/**
 * Construye la URL base del servidor Orthanc
 * @param {Object} config - Configuración del PACS
 * @returns {string} URL completa del servidor
 */
export const buildOrthancUrl = (config) => {
  const { PROTOCOLO, IP, PUERTO, BASE_PATH } = config

  const port =
    (PROTOCOLO === 'https' && PUERTO === '443') ||
    (PROTOCOLO === 'http' && PUERTO === '80')
      ? ''
      : `:${PUERTO}`

  const basePath = BASE_PATH ? BASE_PATH : ''

  return `${PROTOCOLO}://${IP}${port}${basePath}`
}

/**
 * ¿Este PACS debe intentar descarga directa R2 via presigner?
 * @param {Object} config
 * @returns {boolean}
 */
export const isR2DirectEnabled = (config) => Boolean(config && config.R2_DIRECT)

/**
 * URL del handshake r2-manifest para un estudio Orthanc.
 * Presigner: Key R2 por instancia = "${instanceId}.dcm" (flat).
 *
 * @param {Object} config - pacsConfig entry
 * @param {string} studyId - Orthanc study UUID
 * @returns {string}
 */
export const buildR2ManifestUrl = (config, studyId) => {
  const base = buildOrthancUrl(config).replace(/\/$/, '')
  const template =
    (config && config.R2_MANIFEST_PATH) || DEFAULT_R2_MANIFEST_PATH
  const path = String(template).replace(/\{id\}/g, encodeURIComponent(studyId))
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalized}`
}

/**
 * ¿El path ya es una raíz QIDO/WADO-RS (no WADO-URI /wado)?
 * @param {string} pathname
 * @returns {boolean}
 */
export function isDicomWebRootPath(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '') || '/'
  return (
    /\/dicom-web$/i.test(p) ||
    /\/dicomproxy\/rs$/i.test(p) ||
    /\/wado-rs$/i.test(p)
  )
}

/**
 * ¿El path es WADO-URI clásico (/wado)?
 * @param {string} pathname
 * @returns {boolean}
 */
export function isWadoUriPath(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '') || '/'
  return /\/wado$/i.test(p)
}

function stripUrlPath(urlLike) {
  try {
    const u = new URL(String(urlLike))
    const pathname = String(u.pathname || '').replace(/\/+$/, '') || '/'
    return { origin: u.origin, pathname, ok: true }
  } catch (e) {
    return { origin: '', pathname: '', ok: false }
  }
}

/**
 * Raíz DICOMweb (…/dicom-web, …/wado-rs) → sibling WADO-URI (…/wado).
 * Orthanc no sirve requestType=WADO en /dicom-web (HTTP 404).
 * @param {string} url
 * @returns {string}
 */
export function dicomWebRootToWadoUriBase(url) {
  const parsed = stripUrlPath(url)
  if (!parsed.ok) {
    return String(url || '').replace(/\/$/, '')
  }
  const { origin, pathname } = parsed
  if (/\/dicom-web$/i.test(pathname) || /\/wado-rs$/i.test(pathname)) {
    const wadoPath = pathname
      .replace(/\/dicom-web$/i, '/wado')
      .replace(/\/wado-rs$/i, '/wado')
    return `${origin}${wadoPath}`
  }
  return `${origin}${pathname}`
}

/**
 * URL base WADO-URI clásico (requestType=WADO).
 * Si el override es raíz DICOMweb (ticket …/dicom-web), deriva …/wado
 * conservando el prefijo (p. ej. /visorweb).
 * @param {Object} config
 * @param {string|null} [wadoBaseOverride] — ?wadoUrl= del deep-link / ticket
 * @returns {string}
 */
export function buildWadoUriUrl(config, wadoBaseOverride) {
  if (wadoBaseOverride) {
    return dicomWebRootToWadoUriBase(wadoBaseOverride)
  }
  const base = buildOrthancUrl(config).replace(/\/$/, '')
  const path = (config && config.WADO_URI_PATH) || '/wado'
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalized}`
}

/**
 * URL base DICOMweb (QIDO-RS / STOW-RS).
 * Si wadoUrlOverride ya es raíz DICOMweb (p. ej. …/visorweb/dicom-web),
 * se usa tal cual: no reescribir a origin+/dicom-web (rompe CORS/proxy).
 * Si es WADO-URI bajo prefijo (…/visorweb/wado), QIDO queda en …/visorweb/dicom-web.
 * @param {Object} config
 * @param {string|null} [wadoUrlOverride]
 * @returns {string}
 */
export function buildDicomWebBaseUrl(config, wadoUrlOverride) {
  const webPath = (config && config.DICOMWEB_BASE) || '/dicom-web'
  const normalizedPath = webPath.startsWith('/') ? webPath : `/${webPath}`

  if (wadoUrlOverride) {
    try {
      const parsed = stripUrlPath(wadoUrlOverride)
      if (parsed.ok) {
        if (isDicomWebRootPath(parsed.pathname)) {
          return `${parsed.origin}${parsed.pathname}`
        }
        if (isWadoUriPath(parsed.pathname)) {
          return `${parsed.origin}${parsed.pathname.replace(/\/wado$/i, '/dicom-web')}`
        }
        return `${parsed.origin}${normalizedPath}`
      }
    } catch (e) {
      /* fallback a config */
    }
  }

  const base = buildOrthancUrl(config).replace(/\/$/, '')
  return `${base}${normalizedPath}`
}

/**
 * Par QIDO + WADO-URI desde la URL del ticket/override.
 * @param {Object} config
 * @param {string|null} wadoUrlOverride
 * @returns {{ wadoUrl: string, qidoBase: string }}
 */
export function splitLegacyWadoEndpoints(config, wadoUrlOverride) {
  return {
    wadoUrl: buildWadoUriUrl(config, wadoUrlOverride),
    qidoBase: buildDicomWebBaseUrl(config, wadoUrlOverride),
  }
}

/**
 * Contexto legacy WADO-URI autónomo desde query params.
 * @param {URLSearchParams} urlParams
 * @param {Object} pacsConfig
 * @returns {{
 *   wadoUrl: string,
 *   studyUID: string,
 *   seriesUID: string|null,
 *   qidoBase: string,
 * }|null}
 */
export function resolveLegacyWadoContext(urlParams, pacsConfig) {
  if (!urlParams) return null
  const rawWado = urlParams.get('wadoUrl')
  const studyUID = urlParams.get('studyUID')
  if (!rawWado || !studyUID) return null

  let wadoUrl = rawWado
  try {
    wadoUrl = decodeURIComponent(rawWado)
  } catch (e) {
    /* usar valor tal cual */
  }

  const seriesRaw = urlParams.get('seriesUID')
  let seriesUID = seriesRaw
  if (seriesRaw) {
    try {
      seriesUID = decodeURIComponent(seriesRaw)
    } catch (e) {
      /* usar valor tal cual */
    }
  }

  const wadoBase = buildWadoUriUrl(pacsConfig, wadoUrl)
  const qidoBase = buildDicomWebBaseUrl(pacsConfig, wadoUrl)

  return {
    wadoUrl: wadoBase,
    studyUID: String(studyUID).trim(),
    seriesUID: seriesUID ? String(seriesUID).trim() : null,
    qidoBase,
  }
}

/**
 * Obtiene la lista de todos los PACS configurados (excepto DEFAULT)
 * @returns {Array} Lista de IDs de PACS
 */
export const getAvailablePacs = () => {
  return Object.keys(PACS_CONFIG).filter((key) => key !== 'DEFAULT')
}
